import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Location, NgIf } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BaseComponent } from '../../../core/base.component';
import { HeaderService } from '../../../core/services/header.service';
import { AuthService } from '../../../core/security/auth.service';
import { RolesEnum } from '../../../core/security/roles.enum';
import { NotificationService } from '../../../notification/notification.service';
import { CustomReportModel } from '../../../api/models/custom-report-model';
import { CustomReportsService, ReportTypes } from '../custom-reports.service';
import { RRule } from 'rrule';
import { DateUtils } from '../../../core/utilities/date-time-helpers';
import { DateTime } from 'luxon';
import { ReportsService } from '../../reports.service';
import { AgentGroupsSummaryService } from '../../agent-groups-summary/agent-groups-summary.service';
import { CallLogsService } from '../../call-logs/call-logs.service';
import { LookupService } from '../../../api/services/lookup.service';
import { LookupModel } from '../../../api/models/lookup-model';
import { HuntGroupsSummaryService } from '../../hunt-groups-dashboard/hunt-groups-summary.service';
import { UsersSummaryService } from '../../users-summary/users-summary.service';
import { MatDialog } from '@angular/material/dialog';
import { MatConfirmDialogComponent } from '../../../dialogs/mat-confirm/mat-confirm-dialog.component';
import { MatConfirmHtmlDialogComponent } from '../../../dialogs/mat-confirm-html/mat-confirm-html-dialog.component';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { CurrentUser } from '../../../api/models';
import { CallLogsV2Service } from '../../call-logs-v2/call-logs-v2.service';
import { UsersSummaryV2Service } from '../../users-summary-v2/users-summary-v2.service';
import { GroupsSummaryV2Service } from '../../groups-summary-v2/groups-summary-v2.service';
import { MatStepper, MatStep, MatStepperNext, MatStepperPrevious } from '@angular/material/stepper';
import { MatCheckboxInputComponent } from '../../../editors/mat-checkbox-input/mat-checkbox-input.component';
import { MatTextInputComponent } from '../../../editors/mat-text-input/mat-text-input.component';
import { MatSimpleSelectComponent } from '../../../editors/mat-simple-select/mat-simple-select.component';
import { MatButton } from '@angular/material/button';
import { MatPhoneNumberInputComponent } from '../../../editors/mat-phone-number-input/mat-phone-number-input.component';
import { CardOptionsComponent } from '../../../core/visual/card/options-card.component';
import { MatRruleFormGroupComponent } from '../../../editors/mat-rrule-form-group/mat-rrule-form-group.component';
import { MatMultiselectComponent } from '../../../editors/mat-multiselect-input/mat-multiselect.component';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-custom-report-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatStepper,
    MatStep,
    MatCheckboxInputComponent,
    MatTextInputComponent,
    MatSimpleSelectComponent,
    MatButton,
    MatStepperNext,
    MatStepperPrevious,
    MatPhoneNumberInputComponent,
    CardOptionsComponent,
    MatRruleFormGroupComponent,
    MatMultiselectComponent,
    Angular2PromiseButtonModule,
    MatTooltip,
    MatIcon,
  ],
})
export class CustomReportEditComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  @Output() afterSaveOrDelete: EventEmitter<CustomReportModel> = new EventEmitter();
  @Input() id: string;
  popupMode = false;

  form: UntypedFormGroup;
  formGeneral: UntypedFormGroup;
  formRecipients: UntypedFormGroup;
  recurringForm: UntypedFormGroup;
  formTimeRange: UntypedFormGroup;

  submitted: boolean;
  serverError: boolean;
  model: CustomReportModel = {
    name: '',
    sourceName: '',
    id: 'new',
    isEnabled: true,
    recipients: [],
  };
  userTimezone = 'America/New_York';
  buttonSubmitPromise: Promise<void>;
  allRecipients: LookupModel[];
  timeRanges: LookupModel[] = [
    { id: 0, text: 'Today' },
    { id: 1, text: 'Yesterday' },
    { id: 2, text: 'This Week' },
    { id: 3, text: 'Last Week' },
    { id: 4, text: 'This Month' },
    { id: 5, text: 'Last Month' },
    { id: 6, text: 'Last 10 days' },
    { id: 7, text: 'Last 20 days' },
    { id: 8, text: 'Last 30 days' },
    { id: 9, text: 'Last 60 days' },
    { id: 10, text: 'Last 180 days' },
  ];
  daysOfWeek: LookupModel[] = [
    { id: 1, text: 'Monday' },
    { id: 2, text: 'Tuesday' },
    { id: 3, text: 'Wednesday' },
    { id: 4, text: 'Thursday' },
    { id: 5, text: 'Friday' },
    { id: 6, text: 'Saturday' },
    { id: 7, text: 'Sunday' },
  ];
  recurringNextDate: Date;
  rRule: RRule;
  fitterByFromNumber = '';
  fitterByToNumber = '';
  otherAccountsOptions: any[] = [];
  checkedAgentGroupsOptions: any[] = [];
  checkedAgentsOptions: any[] = [];
  checkedExtensionsOptions: any[] = [];
  checkedDepartmentsOptions: any[] = [];
  checkedDomainsOptions: any[] = [];
  exportAgentGroupsColumns: any[] = [];
  exportAgentsColumns: any[] = [];
  exportAgentsV2Columns: any[] = [];
  exportCallLogsColumns: any[] = [];
  exportCallLogsV2Columns: any[] = [];
  exportAgentsLoginLogoutColumns: any[] = [];
  selectedAgentGroupsColumns: any[] = [];
  selectedAgentsColumns: any[] = [];
  selectedAgentsV2Columns: any[] = [];
  selectedCallLogsColumns: any[] = [];
  selectedCallLogsV2Columns: any[] = [];
  selectedAgentsLoginLogoutColumns: any[] = [];
  checkedHuntGroupsOptions: any[] = [];
  exportHuntGroupsColumns: any[] = [];
  exportHuntAgentsColumns: any[] = [];
  exportHuntAgentsV2Columns: any[] = [];
  selectedHuntGroupsColumns: any[] = [];
  selectedHuntAgentsColumns: any[] = [];
  selectedHuntAgentsV2Columns: any[] = [];
  exportUsersSummaryColumns: any[] = [];
  exportUsersSummaryV2Columns: any[] = [];
  selectedUsersSummaryColumns: any[] = [];
  selectedUsersSummaryV2Columns: any[] = [];
  exportDomainsSummaryColumns: any[] = [];
  selectedDomainsSummaryColumns: any[] = [];
  private paramsSubscription;

  constructor(
    route: ActivatedRoute,
    titleService: Title,
    headerService: HeaderService,
    authService: AuthService,
    public customReportsService: CustomReportsService,
    public reportsService: ReportsService,
    public callLogsService: CallLogsService,
    public callLogsV2Service: CallLogsV2Service,
    public groupsSummaryV2Service: GroupsSummaryV2Service,
    public agentGroupsSummaryService: AgentGroupsSummaryService,
    public huntGroupsSummaryService: HuntGroupsSummaryService,
    public usersSummaryService: UsersSummaryService,
    public usersSummaryV2Service: UsersSummaryV2Service,
    public lookupService: LookupService,
    private changeDetectorService: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    public dialog: MatDialog,
    private location: Location,
    private notificationService: NotificationService,
  ) {
    super(route, headerService, titleService, authService);

    this.viewRoles = [RolesEnum.CustomReportsView, RolesEnum.CustomReportsEdit, RolesEnum.CustomReportsDelete];
    this.editRoles = [RolesEnum.CustomReportsEdit, RolesEnum.CustomReportsDelete];
    this.deleteRoles = [RolesEnum.CustomReportsDelete];

    this.paramsSubscription = this.route.params.subscribe(async params => {
      this.model = {
        name: '',
        id: params['id'],
        sourceName: '',
        isEnabled: true,
        recipients: [],
        dataFilter: { timeRange: 1 },
      };
      console.log(`${this.model.id}`);

      if (this.model.id !== 'new' && this.model.id) {
        this.model = await this.customReportsService.getCustomReportById(this.model.id);
        this.resetForm();
      }
    });

    this.exportAgentGroupsColumns = this.agentGroupsSummaryService.exportColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportAgentsColumns = this.agentGroupsSummaryService.exportAgentsColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportAgentsV2Columns = this.groupsSummaryV2Service.exportAgentsColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportAgentsLoginLogoutColumns = this.groupsSummaryV2Service.exportAgentsLoginLogoutColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportHuntGroupsColumns = this.huntGroupsSummaryService.exportColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportHuntAgentsColumns = this.huntGroupsSummaryService.exportAgentsColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportHuntAgentsV2Columns = this.groupsSummaryV2Service.exportAgentsColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportUsersSummaryColumns = this.usersSummaryService.exportColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportUsersSummaryV2Columns = this.usersSummaryV2Service.exportColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportDomainsSummaryColumns = this.usersSummaryService.exportDomainsColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportCallLogsColumns = this.callLogsService.allExportColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    this.exportCallLogsV2Columns = this.callLogsV2Service.allExportColumns.map((x: any) => {
      return {
        text: x.title,
        value: x.id,
        field: x.field,
        hidden: x.hidden,
        id: x.id,
      };
    });

    if (this.model.id === 'new') {
      this.selectedAgentGroupsColumns = this.exportAgentGroupsColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedAgentsColumns = this.exportAgentsColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedAgentsV2Columns = this.exportAgentsV2Columns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedAgentsLoginLogoutColumns = this.exportAgentsLoginLogoutColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedCallLogsColumns = this.exportCallLogsColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedCallLogsV2Columns = this.exportCallLogsV2Columns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedHuntGroupsColumns = this.exportHuntGroupsColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedHuntAgentsColumns = this.exportHuntAgentsColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedHuntAgentsV2Columns = this.exportHuntAgentsV2Columns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedUsersSummaryColumns = this.exportUsersSummaryColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedUsersSummaryV2Columns = this.exportUsersSummaryV2Columns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);

      this.selectedDomainsSummaryColumns = this.exportDomainsSummaryColumns
        .filter(item => {
          return !item.hidden;
        })
        .map(item => item.id);
    }

    this.createForm();
  }

  onCurrentUserChanged(user: CurrentUser) {
    super.onCurrentUserChanged(user);

    this.userTimezone = user.settings.timezone || 'America/New_York';
  }

  async ngOnInit() {
    super.ngOnInit();
    this.headerService.showBackButton(true);
    this.onDeleteProcess = this.onDeleteProcess.bind(this);

    this.ngOnChanges();
  }

  ngOnChanges() {
    if (!!this.id) {
      this.popupMode = true;
      this.model = {
        name: '',
        id: this.id,
        sourceName: '',
        isEnabled: true,
        recipients: [],
        dataFilter: { timeRange: 1 },
      };
      // this.resetForm();
      if (this.model.id !== 'new') {
        this.customReportsService.getCustomReportById(this.model.id).then(value => {
          this.model = value;
          this.resetForm();
        });
      }
    } else {
      this.popupMode = false;
    }

    this.pullAllUsers().then();

    this.resetForm();
  }

  resetForm() {
    const recurring = this.preFillRecurring(this.model.rRule);

    this.form.reset({
      general: {
        name: this.model.name,
        sourceName: this.model.sourceName,
        isEnabled: this.model.isEnabled,
      },
      recipients: {
        recipients: this.model.recipients,
      },
      recurring: recurring,
      timeRange: {
        timeRange: this.model.dataFilter
          ? this.model.dataFilter.timeRange !== undefined
            ? this.model.dataFilter.timeRange
            : 1
          : 1,
        excludeDow: this.model.dataFilter ? this.model.dataFilter.excludeDow || [] : [],
      },
    });
    this.changeDetectorService.markForCheck();

    if (this.model.dataFilter) {
      this.fitterByFromNumber = this.model.dataFilter.fitterByFromNumber;
      this.fitterByToNumber = this.model.dataFilter.fitterByToNumber;

      this.otherAccountsOptions = this.model.dataFilter.otherAccounts || [];
      this.checkedAgentGroupsOptions = this.model.dataFilter.agentGroups || [];
      this.checkedHuntGroupsOptions = this.model.dataFilter.huntGroups || [];
      this.checkedAgentsOptions = this.model.dataFilter.agents || [];
      this.checkedExtensionsOptions = this.model.dataFilter.extensions || [];
      this.checkedDepartmentsOptions = this.model.dataFilter.departments || [];
      this.checkedDomainsOptions = this.model.dataFilter.domains || [];

      if (this.model.id !== 'new') {
        this.selectedAgentGroupsColumns = this.exportAgentGroupsColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportAgentGroupsColumns &&
              this.model.dataFilter.exportAgentGroupsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedAgentsColumns = this.exportAgentsColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportAgentsColumns &&
              this.model.dataFilter.exportAgentsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedAgentsV2Columns = this.exportAgentsV2Columns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportAgentsColumns &&
              this.model.dataFilter.exportAgentsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedAgentsLoginLogoutColumns = this.exportAgentsLoginLogoutColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportAgentsLoginLogoutColumns &&
              this.model.dataFilter.exportAgentsLoginLogoutColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedCallLogsColumns = this.exportCallLogsColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportCallLogsColumns &&
              this.model.dataFilter.exportCallLogsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedCallLogsV2Columns = this.exportCallLogsV2Columns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportCallLogsColumns &&
              this.model.dataFilter.exportCallLogsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedHuntGroupsColumns = this.exportHuntGroupsColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportHuntGroupsColumns &&
              this.model.dataFilter.exportHuntGroupsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedHuntAgentsColumns = this.exportHuntAgentsColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportHuntAgentsColumns &&
              this.model.dataFilter.exportHuntAgentsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedHuntAgentsV2Columns = this.exportHuntAgentsV2Columns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportHuntAgentsColumns &&
              this.model.dataFilter.exportHuntAgentsColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedUsersSummaryColumns = this.exportUsersSummaryColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportUsersSummaryColumns &&
              this.model.dataFilter.exportUsersSummaryColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedUsersSummaryV2Columns = this.exportUsersSummaryV2Columns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportUsersSummaryColumns &&
              this.model.dataFilter.exportUsersSummaryColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);

        this.selectedDomainsSummaryColumns = this.exportDomainsSummaryColumns
          .filter(item => {
            return (
              !!this.model.dataFilter.exportDomainsSummaryColumns &&
              this.model.dataFilter.exportDomainsSummaryColumns.some(e => e.field === item.field)
            );
          })
          .map(item => item.id);
      }
    }

    console.log('after form reset', this.model);
  }

  preFillRecurring(ruleText?: string) {
    console.log('ruleText: ', ruleText);

    if (!ruleText) {
      return {
        freq: RRule.DAILY,
        interval: 1,
        dtstart: DateTime.local().setZone(this.userTimezone).toJSDate(),
        endType: -1,
        monthlyType: 'bymonthday',
        yearlyType: 'bymonthday',
      };
    } else {
      this.rRule = RRule.fromString(ruleText);
      const ruleOptions = this.rRule.options;

      const date = DateTime.fromJSDate(ruleOptions.dtstart, { zone: this.userTimezone });
      const dtstart = DateUtils.localDateFromDateTime(date) || DateUtils.localDateFromDateTime(DateTime.local());

      return {
        freq: ruleOptions.freq,
        interval: ruleOptions.interval || 1,
        dtstart: dtstart,
        endType: ruleOptions.until ? 2 : ruleOptions.count ? 1 : -1,
        until: ruleOptions.until || undefined,
        count: ruleOptions.count || 1,
        byDay: ruleOptions.byweekday || undefined,
        byMonthDay:
          !!ruleOptions.bymonthday && ruleOptions.bymonthday.length > 0 ? ruleOptions.bymonthday[0] : undefined,
        bySetPos: !!ruleOptions.bysetpos && ruleOptions.bysetpos.length > 0 ? ruleOptions.bysetpos[0] : undefined,
        monthlyType: !!ruleOptions.bymonthday && ruleOptions.bymonthday.length > 0 ? 'bymonthday' : 'bysetpos',
        yearlyType: !!ruleOptions.bymonthday && ruleOptions.bymonthday.length > 0 ? 'bymonthday' : 'bysetpos',
        byMonth: !!ruleOptions.bymonth && ruleOptions.bymonth.length > 0 ? ruleOptions.bymonth[0] : undefined,
      };
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.paramsSubscription?.unsubscribe();
    this.headerService.showBackButton(false);
  }

  async onSubmit() {
    this.submitted = false;
    this.serverError = false;

    if (this.form.valid) {
      this.buttonSubmitPromise = this.processSubmit();
    } else {
      let allErrors = '';
      for (const controlName of Object.getOwnPropertyNames(this.form.controls)) {
        const control = this.form.controls[controlName];
        if (!!control && !!control.errors) {
          for (const controlError of Object.getOwnPropertyNames(control.errors)) {
            if (controlError === 'required') {
              allErrors += `<li>${controlName} is required</li>`;
            } else {
              allErrors += `<li>${controlName} is ${controlError}</li>`;
            }
          }
        }
      }
      console.log(this.form.errors);

      this.dialog.open(MatConfirmHtmlDialogComponent, {
        // width: '250px',
        data: {
          title: 'Attention!',
          html: `Your custom report is missing required fields <ul>${allErrors}</ul>`,
          actionTitle: 'Got It',
          hideCancel: true,
        },
      });
    }
  }

  async processSubmit(): Promise<void> {
    try {
      this.submitted = true;
      const reqData = this.prepareFormModel();
      await this.customReportsService.saveCustomReport(reqData);
      this.notificationService.notifySuccess('Record updated successfully');

      if (this.popupMode) {
        this.afterSaveOrDelete.emit(reqData);
      } else {
        this.goBack();
      }
    } catch (e) {
      this.serverError = true;
    } finally {
      this.submitted = false;
    }
  }

  prepareFormModel(): CustomReportModel {
    const formModel = this.form.value;

    const exAgentGroupsColumns = this.agentGroupsSummaryService.exportColumns.filter(x => {
      return this.selectedAgentGroupsColumns.indexOf(x.id) > -1;
    });

    const exAgentsColumns = this.agentGroupsSummaryService.exportAgentsColumns.filter(x => {
      return this.selectedAgentsColumns.indexOf(x.id) > -1;
    });

    const exAgentsV2Columns = this.groupsSummaryV2Service.exportAgentsColumns.filter(x => {
      return this.selectedAgentsV2Columns.indexOf(x.id) > -1;
    });

    const exAgentsLoginLogoutColumns = this.groupsSummaryV2Service.exportAgentsLoginLogoutColumns.filter(x => {
      return this.selectedAgentsLoginLogoutColumns.indexOf(x.id) > -1;
    });

    const exCallLogsColumns = this.callLogsService.allExportColumns.filter(x => {
      return this.selectedCallLogsColumns.indexOf(x.id) > -1;
    });

    const exCallLogsV2Columns = this.callLogsV2Service.allExportColumns.filter(x => {
      return this.selectedCallLogsV2Columns.indexOf(x.id) > -1;
    });

    const exHuntGroupsColumns = this.huntGroupsSummaryService.exportColumns.filter(x => {
      return this.selectedHuntGroupsColumns.indexOf(x.id) > -1;
    });

    const exHuntAgentColumns = this.huntGroupsSummaryService.exportAgentsColumns.filter(x => {
      return this.selectedHuntAgentsColumns.indexOf(x.id) > -1;
    });

    const exHuntAgentV2Columns = this.groupsSummaryV2Service.exportAgentsColumns.filter(x => {
      return this.selectedHuntAgentsV2Columns.indexOf(x.id) > -1;
    });

    const exUsersSummaryColumns = this.usersSummaryService.exportColumns.filter(x => {
      return this.selectedUsersSummaryColumns.indexOf(x.id) > -1;
    });

    const exUsersSummaryV2Columns = this.usersSummaryV2Service.exportColumns.filter(x => {
      return this.selectedUsersSummaryV2Columns.indexOf(x.id) > -1;
    });

    const exDomainsSummaryColumns = this.usersSummaryService.exportDomainsColumns.filter(x => {
      return this.selectedDomainsSummaryColumns.indexOf(x.id) > -1;
    });

    return {
      id: this.model.id,
      name: formModel.general.name,
      sourceName: formModel.general.sourceName,
      isEnabled: formModel.general.isEnabled,
      recipients: formModel.recipients.recipients,
      rRule: this.rRule ? this.rRule.toString() : undefined,
      nextRecurringDate: this.recurringNextDate ? this.recurringNextDate.toISOString() : undefined,
      dataFilter: {
        timeRange: formModel.timeRange.timeRange,
        excludeDow: formModel.timeRange.excludeDow,
        agentGroups:
          this.checkedAgentGroupsOptions.length === this.reportsService.groupsLookup.length
            ? []
            : this.checkedAgentGroupsOptions,
        otherAccounts:
          this.otherAccountsOptions.length === this.reportsService.groupsAndOtherLookup.length
            ? []
            : this.otherAccountsOptions,
        huntGroups: this.checkedHuntGroupsOptions,
        agents:
          this.checkedAgentsOptions.length === this.reportsService.extensionsLookup.length
            ? []
            : this.checkedAgentsOptions,
        extensions: this.checkedExtensionsOptions,
        departments: this.checkedDepartmentsOptions,
        domains: this.checkedDomainsOptions,
        fitterByFromNumber: this.fitterByFromNumber,
        fitterByToNumber: this.fitterByToNumber,
        exportAgentGroupsColumns: exAgentGroupsColumns,
        exportAgentsColumns: formModel.general.sourceName.indexOf('V2') > -1 ? exAgentsV2Columns : exAgentsColumns,
        exportCallLogsColumns:
          formModel.general.sourceName.indexOf('V2') > -1 ? exCallLogsV2Columns : exCallLogsColumns,
        exportHuntGroupsColumns: exHuntGroupsColumns,
        exportHuntAgentsColumns:
          formModel.general.sourceName.indexOf('V2') > -1 ? exHuntAgentV2Columns : exHuntAgentColumns,
        exportUsersSummaryColumns:
          formModel.general.sourceName.indexOf('V2') > -1 ? exUsersSummaryV2Columns : exUsersSummaryColumns,
        exportDomainsSummaryColumns: exDomainsSummaryColumns,
        exportAgentsLoginLogoutColumns: exAgentsLoginLogoutColumns,
      },
    };
  }

  goBack(): void {
    this.location.back();
  }

  createForm(): void {
    this.formGeneral = this.fb.group({
      isEnabled: [true],
      name: ['', Validators.required],
      sourceName: ['', Validators.required],
    });

    this.formRecipients = this.fb.group({
      recipients: [[], Validators.required],
    });

    this.formTimeRange = this.fb.group({
      timeRange: [1, Validators.required],
      excludeDow: [[], []],
    });

    this.recurringForm = this.fb.group({
      freq: [RRule.DAILY],
      interval: [1],
      byDay: [],
      byMonthDay: [],
      byMonth: [],
      monthlyType: ['bymonthday'],
      yearlyType: [],
      bySetPos: [1],
      endType: [-1],
      count: [1],
      until: [],
      dtstart: [],
    });

    this.form = this.fb.group({
      general: this.formGeneral,
      recipients: this.formRecipients,
      timeRange: this.formTimeRange,
      recurring: this.recurringForm,
    });
  }

  async pullAllUsers(): Promise<void> {
    const response = await this.lookupService.lookupGetCustomerUsers({ onlyFax: false, onlySms: false }).toPromise();

    this.allRecipients = response.data;
  }

  async onDeleteClick() {
    const dialogRef = this.dialog.open(MatConfirmDialogComponent, {
      // width: '250px',
      data: {
        title: 'Delete Report',
        text: 'Are you sure you want to delete this Report?',
        actionTitle: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.onDeleteProcess();
      }
    });
  }

  async onDeleteProcess(): Promise<void> {
    await this.customReportsService.deleteCustomReportById(this.model.id);
    this.notificationService.notifySuccess('Record has been deleted successfully');
    if (this.popupMode) {
      this.afterSaveOrDelete.emit(undefined);
    } else {
      this.headerService.goBack();
    }
  }

  onRRuleChanged($event: [RRule, Date]) {
    [this.rRule, this.recurringNextDate] = $event;
  }

  protected readonly ReportTypes = ReportTypes;
}
